/**
 * Room definitions API. Read access for any authenticated staff member
 * (the generation/validation engine needs this data regardless of role).
 * Write access is Admin-only — enforced by RLS (migrations/0003), not just
 * hidden in the UI. Coordinators calling the write mutations here would
 * get a database error, not a silent no-op; the Admin-only pages are the
 * only place these mutations should be wired up in the UI.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import type { ExclusionZone, RoomDefinition } from '../domain/types';
import type { Database } from '../lib/database.types';

type RoomDefinitionRow = Database['public']['Tables']['room_definitions']['Row'];

function rowToRoomDefinition(row: RoomDefinitionRow): RoomDefinition {
  return {
    room: row.room,
    displayName: row.display_name,
    widthFt: row.width_ft,
    lengthFt: row.length_ft,
    entrance: {
      x: row.entrance_x,
      y: row.entrance_y,
      facing: row.entrance_facing,
    },
    exclusionZones: row.exclusion_zones as unknown as ExclusionZone[],
  };
}

const ROOM_DEFINITIONS_KEY = ['room-definitions'] as const;

export function useRoomDefinitions() {
  return useQuery({
    queryKey: ROOM_DEFINITIONS_KEY,
    queryFn: async (): Promise<RoomDefinition[]> => {
      const { data, error } = await supabase.from('room_definitions').select('*').order('room');
      if (error) throw error;
      return data.map(rowToRoomDefinition);
    },
    // This data changes rarely (Admin-edited only) — cache generously so
    // every editor/wizard screen doesn't re-fetch it constantly.
    staleTime: 5 * 60 * 1000,
  });
}

export interface UpdateRoomDefinitionInput {
  room: RoomDefinition['room'];
  displayName?: string;
  widthFt?: number;
  lengthFt?: number;
  entrance?: { x: number; y: number; facing: number };
  exclusionZones?: ExclusionZone[];
}

/** Admin-only — see file header. */
export function useUpdateRoomDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateRoomDefinitionInput): Promise<RoomDefinition> => {
      const { room, entrance, exclusionZones, ...rest } = input;
      const { data, error } = await supabase
        .from('room_definitions')
        .update({
          ...(rest.displayName !== undefined && { display_name: rest.displayName }),
          ...(rest.widthFt !== undefined && { width_ft: rest.widthFt }),
          ...(rest.lengthFt !== undefined && { length_ft: rest.lengthFt }),
          ...(entrance !== undefined && {
            entrance_x: entrance.x,
            entrance_y: entrance.y,
            entrance_facing: entrance.facing,
          }),
          ...(exclusionZones !== undefined && { exclusion_zones: exclusionZones }),
        })
        .eq('room', room)
        .select()
        .single();

      if (error) throw error;
      return rowToRoomDefinition(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROOM_DEFINITIONS_KEY });
    },
  });
}
